import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import TeamMember from './TeamMember'

const Container = styled.div`
  text-align: center;
`

const TeamMembersBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`

const TeamMemberContainer = styled.div`
  display: inline-flex;
  flex: 1 0 25%;
  max-width: 25%;
  padding: 1rem;
  justify-content: center;

  @media screen and (max-width: 1000px) {
    flex-basis: 33.3333333%;
    max-width: 33.3333333%;
  }

  @media screen and (max-width: 800px) {
    flex-basis: 50%;
    max-width: 50%;
  }

  @media screen and (max-width: 600px) {
    flex-basis: 100%;
    max-width: 100%;
  }
`

const TeamTitle = styled.h2`
  color: ${({ theme }) => theme.colors.darkText};
  position: relative;
  display: inline-block;

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    border-bottom: 0.1rem solid ${({ theme }) => theme.colors.darkText};
    width: 3rem;
    display: block;
  }

  &:before {
    left: -1.5rem;
    transform: translate(-100%, -50%);
  }

  &:after {
    right: -1.5rem;
    transform: translate(100%, -50%);
  }

  @media screen and (max-width: 800px) {
    &:before,
    &:after {
      display: none;
    }
  }
`

const NoResults = styled.p`
  margin: 8rem 0 4rem;
  text-align: center;
`

const TeamMembers = ({
  teamLabels,
  noResults,
  teamMembers,
  includeDepartmentTitle
}) => {
  const getSortedTeamMembers = () => {
    const sortedTeamMembers = {}
    teamLabels.forEach(({ key }) => {
      sortedTeamMembers[key] = teamMembers.filter(
        (member) => member.team === key
      )
    })

    return sortedTeamMembers
  }

  const getTeamLabel = (key) => {
    let label
    const teamLabelsClone = [...teamLabels]
    while (!label && teamLabelsClone.length > 0) {
      const nextLabel = teamLabelsClone.shift()
      if (nextLabel.key === key) label = nextLabel.label
    }

    return label || 'N/A'
  }

  const sortedTeamMembers = getSortedTeamMembers()

  return (
    <Container>
      {!includeDepartmentTitle && teamMembers.length > 0 && (
        <TeamMembersBlock>
          {Object.keys(sortedTeamMembers)
            .map((key) => sortedTeamMembers[key])
            .reduce((a, b) => [...a, ...b])
            .map((member) => (
              <TeamMemberContainer key={member.slug}>
                <TeamMember key={member.slug} {...member} />
              </TeamMemberContainer>
            ))}
        </TeamMembersBlock>
      )}
      {includeDepartmentTitle &&
        teamMembers.length > 0 &&
        Object.keys(sortedTeamMembers).map((key) => (
          <div key={key}>
            {sortedTeamMembers[key].length > 0 && (
              <div>
                <TeamTitle>{getTeamLabel(key)}</TeamTitle>
                <TeamMembersBlock>
                  {sortedTeamMembers[key].map((member) => (
                    <TeamMemberContainer key={member.slug}>
                      <TeamMember key={member.slug} {...member} />
                    </TeamMemberContainer>
                  ))}
                </TeamMembersBlock>
              </div>
            )}
          </div>
        ))}
      {teamMembers.length === 0 && (
        <NoResults>
          <em>{noResults}</em>
        </NoResults>
      )}
    </Container>
  )
}

TeamMembers.propTypes = {
  teamLabels: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired
    })
  ).isRequired,
  teamMembers: PropTypes.arrayOf(
    PropTypes.shape({
      team: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired
    })
  ).isRequired,
  noResults: PropTypes.string.isRequired,
  includeDepartmentTitle: PropTypes.bool
}

TeamMembers.defaultProps = {
  includeDepartmentTitle: true
}

export default TeamMembers
